import { useState } from 'react';
import {
  Users,
  Copy,
  Check,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { formatINR } from '@/shared/utils/currency';
import { useTeamDsr } from './useBdmData';

interface AsmTeamDsrCardProps {
  onSelectRep?: (userId: string, repName: string) => void;
}

const todayIso = () => new Date().toISOString().split('T')[0];
const yesterdayIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

export function AsmTeamDsrCard({ onSelectRep }: AsmTeamDsrCardProps) {
  const [selectedDate, setSelectedDate] = useState<string>(todayIso());
  const [copied, setCopied] = useState(false);

  const { data, isLoading, isError } = useTeamDsr(selectedDate);
  const team = data?.team ?? [];

  const totals = team.reduce(
    (acc, r) => ({
      newLeads: acc.newLeads + r.newLeads,
      followupsDone: acc.followupsDone + r.followupsDone,
      visitsDone: acc.visitsDone + r.visitsDone,
      quotationsCount: acc.quotationsCount + r.quotationsCount,
      quotationValue: acc.quotationValue + r.quotationValue,
      ordersCount: acc.ordersCount + r.ordersCount,
      orderValue: acc.orderValue + r.orderValue,
    }),
    {
      newLeads: 0,
      followupsDone: 0,
      visitsDone: 0,
      quotationsCount: 0,
      quotationValue: 0,
      ordersCount: 0,
      orderValue: 0,
    }
  );

  const handleCopyTeamDsr = async () => {
    if (team.length === 0) return;
    const lines = [
      `*TEAM DAILY SALES REPORT (DSR)*`,
      `📅 Date: ${selectedDate}`,
      `👥 Active Team Members: ${team.length}`,
      `──────────────────────────────`,
      `📊 *Total Team Summary:*`,
      `• New Enquiries: ${totals.newLeads}`,
      `• Follow-ups Done: ${totals.followupsDone}`,
      `• Client Visits: ${totals.visitsDone}`,
      `• Quotes Sent: ${totals.quotationsCount} (₹${totals.quotationValue.toLocaleString('en-IN')})`,
      `• Orders Booked: ${totals.ordersCount} (₹${totals.orderValue.toLocaleString('en-IN')})`,
      `──────────────────────────────`,
      `👤 *Rep-wise Breakdown:*`,
      ...team.map(
        (r) =>
          `*${r.name}* (${r.designation || r.role}): Leads: ${r.newLeads} | Followups: ${r.followupsDone} | Visits: ${r.visitsDone} | Quotes: ${r.quotationsCount} (₹${r.quotationValue.toLocaleString('en-IN')}) | Orders: ${r.ordersCount} (₹${r.orderValue.toLocaleString('en-IN')})`
      ),
    ];
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Team DSR summary copied!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy report.');
    }
  };

  return (
    <div className="rounded-card border border-border bg-card p-5 shadow-xs transition hover:shadow-xs">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-container text-primary">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-body-md font-semibold text-on-surface">Area Sales Team DSR Monitor</h3>
            <p className="text-body-sm text-on-surface-variant">
              Live daily sales activity & closures across all assigned BDMs & field staff
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-border bg-surface-variant p-1">
            <button
              type="button"
              onClick={() => setSelectedDate(todayIso())}
              className={`rounded-lg px-2.5 py-1 text-body-sm font-semibold transition ${
                selectedDate === todayIso()
                  ? 'bg-primary text-on-primary shadow-xs'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setSelectedDate(yesterdayIso())}
              className={`rounded-lg px-2.5 py-1 text-body-sm font-semibold transition ${
                selectedDate === yesterdayIso()
                  ? 'bg-primary text-on-primary shadow-xs'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              Yesterday
            </button>
          </div>

          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="rounded-lg border border-border bg-card px-2.5 py-1 text-body-sm font-medium text-on-surface-variant shadow-xs focus:border-primary/40 focus:outline-none"
          />

          <button
            type="button"
            onClick={handleCopyTeamDsr}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-body-sm font-semibold text-on-surface-variant hover:bg-surface-variant hover:text-on-surface transition active:scale-95 shadow-xs"
            title="Copy formatted Team DSR"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-success-text" /> : <Copy className="h-3.5 w-3.5 text-on-surface-variant" />}
            <span>{copied ? 'Copied!' : 'Copy Team DSR'}</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-body-sm text-on-surface-subtle">Loading team DSR data...</div>
      ) : isError ? (
        <div className="py-8 text-center text-body-sm text-danger-text">Could not load team DSR report.</div>
      ) : (
        <div className="mt-4 space-y-4">
          {/* Team Totals Summary Grid */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <div className="rounded-lg border border-border bg-surface-variant/50 p-3">
              <span className="text-data-xs font-semibold uppercase tracking-wider text-on-surface-variant">Team Leads</span>
              <div className="mt-1 text-data-lg font-semibold text-on-surface tabular-nums">{totals.newLeads}</div>
            </div>
            <div className="rounded-lg border border-border bg-surface-variant/50 p-3">
              <span className="text-data-xs font-semibold uppercase tracking-wider text-on-surface-variant">Follow-ups</span>
              <div className="mt-1 text-data-lg font-semibold text-on-surface tabular-nums">{totals.followupsDone}</div>
            </div>
            <div className="rounded-lg border border-border bg-surface-variant/50 p-3">
              <span className="text-data-xs font-semibold uppercase tracking-wider text-on-surface-variant">Visits Done</span>
              <div className="mt-1 text-data-lg font-semibold text-on-surface tabular-nums">{totals.visitsDone}</div>
            </div>
            <div className="rounded-lg border border-border bg-surface-variant/50 p-3">
              <span className="text-data-xs font-semibold uppercase tracking-wider text-on-surface-variant">Quotations</span>
              <div className="mt-1 flex items-baseline gap-1.5">
                <span className="text-data-lg font-semibold text-on-surface tabular-nums">{totals.quotationsCount}</span>
                <span className="text-data-xs font-semibold text-on-surface-variant tabular-nums">{formatINR(totals.quotationValue)}</span>
              </div>
            </div>
            <div className="rounded-lg border border-success/30 bg-success/10 p-3">
              <span className="text-data-xs font-semibold uppercase tracking-wider text-success-text">Orders Booked</span>
              <div className="mt-1 flex items-baseline gap-1.5">
                <span className="text-data-lg font-semibold text-success-text tabular-nums">{totals.ordersCount}</span>
                <span className="text-data-xs font-semibold text-success-text tabular-nums">{formatINR(totals.orderValue)}</span>
              </div>
            </div>
          </div>

          {/* Reps Scoreboard Table */}
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-body-sm">
              <thead>
                <tr className="border-b border-border bg-surface-variant text-left text-data-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                  <th className="py-2.5 px-3">Sales Executive</th>
                  <th className="py-2.5 px-2 text-center">New Leads</th>
                  <th className="py-2.5 px-2 text-center">Follow-ups</th>
                  <th className="py-2.5 px-2 text-center">Visits</th>
                  <th className="py-2.5 px-2 text-right">Quotations</th>
                  <th className="py-2.5 px-3 text-right">Orders Booked</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {team.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-body-sm text-on-surface-subtle">
                      No sales representatives found in team.
                    </td>
                  </tr>
                ) : (
                  team.map((r) => (
                    <tr
                      key={r.userId}
                      onClick={() => onSelectRep?.(r.userId, r.name)}
                      className={`hover:bg-surface-variant/80 transition ${
                        onSelectRep ? 'cursor-pointer' : ''
                      }`}
                    >
                      <td className="py-2.5 px-3">
                        <div className="font-semibold text-on-surface">{r.name}</div>
                        <div className="flex flex-wrap items-center gap-1.5 text-data-xs text-on-surface-subtle capitalize">
                          <span>{r.designation || r.role || 'Sales Rep'}</span>
                          {r.phone && (
                            <div className="inline-flex items-center gap-1">
                              <a
                                href={`tel:${r.phone}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-0.5 rounded bg-surface-container px-1.5 py-0.5 text-data-xs font-mono font-medium text-primary-dim hover:bg-primary-container hover:underline"
                                title={`Call ${r.name}: ${r.phone}`}
                              >
                                <span>📞</span>
                                <span>{r.phone}</span>
                              </a>
                              <a
                                href={`https://wa.me/${r.phone.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center rounded bg-success/10 px-1 py-0.5 text-data-xs text-success-text hover:bg-emerald-100"
                                title={`WhatsApp ${r.name}: ${r.phone}`}
                              >
                                <span>💬</span>
                              </a>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-2 text-center font-semibold tabular-nums text-on-surface-variant">
                        {r.newLeads}
                      </td>
                      <td className="py-2.5 px-2 text-center font-semibold tabular-nums text-on-surface-variant">
                        {r.followupsDone}
                      </td>
                      <td className="py-2.5 px-2 text-center font-semibold tabular-nums text-on-surface-variant">
                        {r.visitsDone}
                      </td>
                      <td className="font-mono py-2.5 px-2 text-right tabular-nums text-data-sm">
                        <span className="font-semibold tabular-nums text-on-surface">{r.quotationsCount}</span>
                        {r.quotationValue > 0 && (
                          <span className="ml-1 text-data-xs text-on-surface-subtle">
                            ({formatINR(r.quotationValue)})
                          </span>
                        )}
                      </td>
                      <td className="font-mono py-2.5 px-3 text-right tabular-nums text-data-sm">
                        <span className="font-semibold tabular-nums text-success-text">{r.ordersCount}</span>
                        {r.orderValue > 0 && (
                          <span className="ml-1 text-data-xs font-semibold text-success-text">
                            ({formatINR(r.orderValue)})
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
